import React, { useState, useEffect } from 'react';
import { Grid, Button, Typography, Box, Divider } from '@material-ui/core';
import { AddBox, ArrowForward, Delete, Edit, Terrain } from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import PropTypes from 'prop-types';

import { useMonitoringPlanApi, useProjectApi } from 'src/services';
import { TitleContainer, ContentContainer, Cards, ActionsBox, DialogWrap, AlertWrapper, AccessControl } from 'src/components';
import { ChartBar, FormatListGroup, ViewColumnOutline } from 'src/utils/mdIcons';
import { SmaMonitoringPlanCreateDialog, SmaMonitoringPlanDeleteDialog, SmaMonitoringPlanCard, SmaMonitoringPlanEditDialog }
  from 'src/scenes/Project/scenes/MonitoringPlans/components';
import { Pagination, PaginationItem } from '@material-ui/lab';
import { useFetchStatus, useFilters } from 'src/hooks';
import { AnalyticCard } from 'src/scenes/Project/components';
import { ProjectMetrics } from 'src/scenes/Project/scenes/Dashboard/components';


const useStyles = makeStyles(theme => ({
  content: {
    maxWidth: theme.spacing(140),
  },
  mainContainer: {
    width: 'calc(100%)',
  },
  comparisonBtn: {
    marginRight: theme.spacing(1),
  },
  link: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    cursor: 'pointer',
  },
  defaultItem: {
    padding: theme.spacing(1),
  },
}));

const SmaMonitoringPlansContainer = ({ project }) => {

  const match = useRouteMatch();
  const history = useHistory();
  const { search } = useLocation();
  const classes = useStyles();
  const monitoringPlanApi = useMonitoringPlanApi();
  const projectApi = useProjectApi();

  const [ monitoringPlans, setMonitoringPlans ] = useState([]);
  const [ sitesOptions, setSitesOptions ] = useState([]);
  const [ monitoringPlansCount, setMonitoringPlansCount ] = useState(0);
  const [ dialog, setDialog ] = useState({ isOpen: false, data: {}, type: '' });
  const { fetchStatus: fetchMonitoringPlansStatus, setFetchStatus, fetchStatusEnum } = useFetchStatus();
  const [ indicators, setIndicators ] = useState({});
  const [ rcas, setRcas ] = useState([]);

  const goToPlanComparison = () => history.push('monitoring-plans/comparison');

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const getQueryParams = () => {
    const query = new URLSearchParams(location.search);
    const fixedSize = 10;
    query.set('size', fixedSize);
    const page = parseInt(query.get('page') || 1, 10);
    return { page, size: fixedSize };
  };

  const { setFilter, query } = useFilters({ history, location });
  const { page, size } = getQueryParams();
  const totalPages = Math.ceil(monitoringPlansCount / size);

  const fetchMonitoringPlans = async () => {
    setFetchStatus(fetchStatusEnum.LOADING);
    query.set('folderId', projectId);
    isSite && query.set('siteId', siteId);
    const { monitoringPlansCount, monitoringPlanList } = await monitoringPlanApi.getMonitoringPlans(query.toString());
    setMonitoringPlans(monitoringPlanList);
    setMonitoringPlansCount(monitoringPlansCount);
    setFetchStatus(fetchStatusEnum.SUCCESS);
  };

  const fetchSitesOptions = async () => {
    const sites = !isSite ? (await projectApi.getSites(projectId)).map(s => ({ label: s.name, value: s.id })) : [];
    setSitesOptions(sites);
  };

  const { isLoading: isLoadingProjectMetrics, handleConfirm: fetchProjectMetrics } = useFetchStatus({
    confirmFn: async () => {
      const [ generalInfo, rcas ] = await Promise.all([
        projectApi.getGeneralInfo({ projectId }),
        projectApi.rcaSearch({ projectId, params: { limit: 0 } }),
      ]);

      if (generalInfo) {
        setIndicators({
          monitoringCount: generalInfo.nMonitoringPlans,
          campaignsCount: generalInfo.nCampaigns,
          replicaStationsCount: generalInfo.nStationReplicate,
        });
      }
      setRcas(rcas.map(r => r.id));
    },
  });

  useEffect(() => {
    fetchProjectMetrics();
  // eslint-disable-next-line
  }, [ monitoringPlans.length ]);

  useEffect(() => {
    fetchSitesOptions();
  // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchMonitoringPlans();
  // eslint-disable-next-line
  }, [ search ]);

  useEffect(() => {
    if (monitoringPlans.length === 0 && page > 1 && fetchMonitoringPlansStatus === fetchStatusEnum.SUCCESS) {
      setFilter({ id: 'page', value: page - 1 });
    }
  }, [ monitoringPlans.length ]); // eslint-disable-line

  const goToAnalytics = monitoringPlanId => history.push(`monitoring-plans/${monitoringPlanId}/analytics`);

  const goToCampaigns = monitoringPlanId => history.push(`campaigns?monitoringPlan=${monitoringPlanId}`);

  const goToGroups = monitoringPlanId => history.push(`monitoring-plans/${monitoringPlanId}/groups`);

  const goToMonitoringPlan = monitoringPlanId => history.push(`monitoring-plans/${monitoringPlanId}`);

  const cardActions = itemData => ([
    { Icon: Terrain, tooltip: 'Ver campañas', onClick: () => goToCampaigns(itemData.id), badgeNumber: itemData.instances.length },
    { Icon: ChartBar, tooltip: 'Ver analíticas', onClick: () => goToAnalytics(itemData.id), badgeNumber: itemData.nCharts },
    { Icon: FormatListGroup, tooltip: 'Ver grupos', onClick: () => goToGroups(itemData.id) },
    !isSite && { Icon: Delete, tooltip: 'Borrar', onClick: () => openDeleteMonitoringPlanDialog(itemData.id),
      accessPermission: 'monitoring-plan:delete' },
    !isSite && { Icon: Edit, tooltip: 'Editar', onClick: () => openEditMonitoringPlanDialog(itemData),
      accessPermission: 'monitoring-plan:edit' },
    { Icon: ArrowForward, tooltip: 'Ver plan de monitoreo', onClick: () => goToMonitoringPlan(itemData.id) },
  ].filter(f => f));

  const createMonitoringPlan = async ({ siteId, name }) => {
    const monitoringPlan = await monitoringPlanApi.createMonitoringPlan({ name, folderId: projectId, siteId });
    setMonitoringPlans(prevMonitoringPlans => [
      monitoringPlan,
      ...(prevMonitoringPlans?.length === size ? prevMonitoringPlans.slice(0, -1) : prevMonitoringPlans),
    ]);
    setMonitoringPlansCount(prevMonitoringPlansCount => prevMonitoringPlansCount + 1);
    if (page > 1) {
      setFilter({ id: 'page', value: 1 });
    }
  };

  const editMonitoringPlan = async data => {
    const { id: monitoringPlanId, siteId, siteName, name, prevSiteSelection } = data;
    await monitoringPlanApi.updateMonitoringPlan({ monitoringPlanId, ...(siteId ? { siteId, prevSiteSelection } : {}), name });
    setMonitoringPlans(prevMonitoringPlans => prevMonitoringPlans.map(monitoringPlan => {
      if (monitoringPlan.id === monitoringPlanId) {
        return {
          ...monitoringPlan,
          name,
          ...(siteId ? { sitesIds: [ siteId ], sitesNames: [ siteName ] } : {}),
        };
      }
      return monitoringPlan;
    }));
  };

  const deleteMonitoringPlan = async monitoringPlanId => {
    await monitoringPlanApi.deleteMonitoringPlan(monitoringPlanId);
    fetchMonitoringPlans();
  };

  const closeDialog = () => setDialog({ isOpen: false, type: '' });

  const openCreateMonitoringPlanDialog = () => setDialog({ isOpen: true, type: 'create' });

  const openDeleteMonitoringPlanDialog = monitoringPlanId => setDialog({ isOpen: true, type: 'delete', data: monitoringPlanId });

  const openEditMonitoringPlanDialog = monitoringPlan => setDialog({ isOpen: true, type: 'edit', data: monitoringPlan });

  const getDialogType = () => {
    switch (dialog.type) {
      case 'create' : {
        return <SmaMonitoringPlanCreateDialog
          options={{ sitesOptions }}
          actions={{ createMonitoringPlan: ({ name, siteId }) => createMonitoringPlan({ name, siteId }), closeDialog }}
        />;
      }
      case 'delete': {
        return <SmaMonitoringPlanDeleteDialog
          actions={{ deleteMonitoringPlan: () => deleteMonitoringPlan(dialog.data), closeDialog }}
        />;
      }
      case 'edit': {
        return <SmaMonitoringPlanEditDialog
          options={{ sitesOptions }}
          monitoringPlan={dialog.data}
          actions={{ editMonitoringPlan: data => editMonitoringPlan(data), closeDialog }}
        />;
      }
      default:
        break;
    }
  };

  return (
    <>
      <Grid container>
        <TitleContainer
          maxWidth='xl'
          project={project}
          breadcrumbs={[ 'projects', 'project', 'sites', 'site', { name: 'Monitoreos SMA' } ]}
        >
          Monitoreos SMA
        </TitleContainer>
        <ContentContainer maxWidth="xl" className={classes.content}>
          {(isLoadingProjectMetrics || (!isSite && indicators.campaignsCount > 0) || (isSite && monitoringPlansCount > 0)) &&
          <Box mb={3}>
            <AnalyticCard title="Métricas SMA">
              <Divider />
              <ProjectMetrics
                indicators={indicators}
                rcas={rcas}
                isLoading={isLoadingProjectMetrics}
              />
            </AnalyticCard>
          </Box>
          }
          {!isSite && <ActionsBox>
            <AccessControl action='monitoring-plan:manage'>
              <Button className={classes.comparisonBtn} size="small" color="primary" startIcon={<ViewColumnOutline />}
                variant="outlined" onClick={goToPlanComparison}>
                Gestionar monitoreos SMA
              </Button>
            </AccessControl>
            <AccessControl action='monitoring-plan:create'>
              <Button size="small" color="primary" startIcon={<AddBox />}
                variant="contained" onClick={openCreateMonitoringPlanDialog}>
                Crear plan de monitoreo SMA
              </Button>
            </AccessControl>
          </ActionsBox>}
          {monitoringPlans.length > 0 ?
            <>
              <Cards
                alwaysShowActions={true}
                values={monitoringPlans}
                actions={cardActions}
                Content={({ itemData }) => <SmaMonitoringPlanCard {...itemData} />}
                classes={{ defaultItem: classes.defaultItem }}
              />
              {monitoringPlansCount > size &&
                <Box pt={3} display="flex" justifyContent="center">
                  <Pagination
                    page={page}
                    count={totalPages}
                    variant="outlined"
                    shape="rounded"
                    color="primary"
                    onChange={(_, page) => {
                      setFilter({ id: 'size', value: size });
                      setFilter({ id: 'page', value: page });
                    }}
                    renderItem={item => (
                      <PaginationItem {...item} />
                    )}
                  />
                </Box>
              }
            </>
            : <Box>
              <AlertWrapper severity="info">
                <Typography variant="body2">
                  No se han creado planes de monitoreo SMA,
                  puedes <Box component="span" className={classes.link} display="inline" onClick={openCreateMonitoringPlanDialog}>crear un
                  plan</Box> o ir al <Box component="span" className={classes.link} display="inline" onClick={goToPlanComparison}>módulo de
                  gestión</Box>
                </Typography>
              </AlertWrapper>
            </Box>

          }
        </ContentContainer>
      </Grid>
      <DialogWrap maxWidth='sm' fullWidth onClose={closeDialog} open={dialog.isOpen}>
        {getDialogType()}
      </DialogWrap>
    </>
  );
};

SmaMonitoringPlansContainer.propTypes = {
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export {
  SmaMonitoringPlansContainer,
};
